// src/pages/Analytics.tsx
import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Model } from "survey-core";
import { Tabulator } from "survey-analytics/survey.analytics.tabulator";
import { collection, getDocs } from "firebase/firestore";
import "tabulator-tables/dist/css/tabulator.css";
import "survey-analytics/survey.analytics.tabulator.css";

import { Box, Typography } from "@mui/material";

import { useSurveyAccess } from "../hooks/useSurveyAccess";
import { useSurveyResponses } from "../hooks/useSurveyResponses";
import PageHeader from "../components/PageHeader";
import { db } from "../firebase";

const cloneSurveyJson = (json: any) => JSON.parse(JSON.stringify(json));

const collectSurveyElements = (elements: any[] | undefined, collector: any[]) => {
  (elements || []).forEach((element) => {
    collector.push(element);
    collectSurveyElements(element.elements, collector);
    collectSurveyElements(element.templateElements, collector);
  });
};

const hydrateFirestoreDropdowns = async (surveyJson: any) => {
  const json = cloneSurveyJson(surveyJson);
  const elements: any[] = [];
  (json.pages || []).forEach((page: any) => collectSurveyElements(page.elements, elements));

  const dropdowns = elements.filter(
    (element) => element?.type === "dropdown" && (element.firestoreListId || element.dataListId)
  );

  await Promise.all(
    dropdowns.map(async (dropdown) => {
      const listId = dropdown.firestoreListId || dropdown.dataListId;
      const itemsSnap = await getDocs(collection(db, "lists", listId, "items"));
      const choices = itemsSnap.docs.map((docSnap) => {
        const item = docSnap.data() as any;
        const value = item.value ?? docSnap.id;
        const text = item.name ?? item.value ?? docSnap.id;
        return { value, text };
      });

      choices.sort((a, b) => String(a.text).localeCompare(String(b.text)));
      dropdown.choices = choices;
    })
  );

  return json;
};

export default function Analytics() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Centralized access check: viewer/editor/owner/admin may analyze
  const { loading, allowed, role, survey } = useSurveyAccess(id!, "analytics");

  const containerRef = useRef<HTMLDivElement>(null);
  const [surveyDef, setSurveyDef] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const { responses, loading: responsesLoading, error: responsesError } = useSurveyResponses(
    id,
    !loading && allowed
  );

  // Load survey JSON (from access hook) & hydrate Firestore-backed dropdowns before rendering responses.
  useEffect(() => {
    let alive = true;
    if (!loading && allowed && survey?.json) {
      setSurveyDef(null);
      setError(null);
      hydrateFirestoreDropdowns(survey.json)
        .then((json) => {
          if (alive) setSurveyDef(json);
        })
        .catch((err) => {
          console.error("Failed to hydrate analytics dropdown choices:", err);
          if (alive) {
            setError(err?.message || "Failed to load analytics dropdown choices.");
          }
        });
    }
    return () => {
      alive = false;
    };
  }, [loading, allowed, survey]);

  // Render the Tabulator table once we have both surveyDef & a container
  useEffect(() => {
    let table: Tabulator | null = null;
    if (surveyDef && containerRef.current) {
      containerRef.current.innerHTML = "";

      const surveyModel = new Model(surveyDef);
      const columns: any[] = [];

      // One column per question
      surveyModel.getAllQuestions().forEach((q: any) => {
        columns.push({
          field: q.name,
          title: q.title || q.name,
        });
      });

      table = new Tabulator(surveyModel, responses, { columns });
      table.setPageSize(25);
      table.render(containerRef.current);
    }
    return () => {
      if (table && typeof table.destroy === "function") {
        try {
          table.destroy();
        } catch (err) {
          // In some cases Tabulator's destroy can throw if the element has already been removed.
          console.warn("Tabulator destroy skipped due to error:", err);
        }
      }
    };
  }, [surveyDef, responses]);

  // Loading / access states
  const combinedError = error || responsesError;

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.key || "").toLowerCase() !== "d") return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const focusEl = e.target as HTMLElement | null;
      if (focusEl) {
        const tag = focusEl.tagName;
        const isFormField = tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || focusEl.isContentEditable;
        if (isFormField) return;
      }
      if (id && allowed) {
        navigate(`/analytics/${id}/debug`);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [id, allowed, navigate]);

  if (loading || responsesLoading) {
    return (
      <Box p={4}>
        <Typography>Checking access…</Typography>
      </Box>
    );
  }
  if (!allowed) {
    return (
      <Box p={4}>
        <Typography color="error">
          You don’t have access to analytics for this survey.
        </Typography>
      </Box>
    );
  }
  if (combinedError) {
    return (
      <Box p={4}>
        <Typography color="error">{combinedError}</Typography>
      </Box>
    );
  }
  if (!surveyDef) {
    return (
      <Box p={4}>
        <Typography>Loading analytics…</Typography>
      </Box>
    );
  }

  // Main UI
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        width: "100%",
        pt: 4,
        px: "2vw",
      }}
    >
      <PageHeader
        title={`Results for “${surveyDef.title || id}”`}
        subtitle={`${responses.length} ${responses.length === 1 ? "response" : "responses"}`}
      />

      <Box
        ref={containerRef}
        sx={{ flexGrow: 1, minHeight: 0, overflow: "auto", p: 2 }}
      />

      <Box sx={{ mt: 2 }}>
        <Typography variant="caption" color="text.secondary">
          Your access: {role}
        </Typography>
      </Box>
    </Box>
  );
}
